import type { OfficeStatus, ZoneType } from "../api/types";
import { STATUS_HEX, STATUS_HEX_DARK } from "./format";
export { useThemeMode, readThemeMode, brandHex } from "../../../components/analytics/theme";
import { useThemeMode, type ThemeMode } from "../../../components/analytics/theme";
export type { ThemeMode };

/**
 * The floor is drawn into a canvas, and a canvas has no cascade: nothing in it
 * inherits a CSS variable, a `dark:` utility or a token. Every colour has to be
 * chosen in JavaScript at draw time, which is why the whole palette lives here
 * as data rather than scattered through the renderer.
 *
 * Two complete palettes rather than one set of colours with an alpha tweak: a
 * wash that reads as "a room" over white is invisible over near-black, and ink
 * that reads over near-black is a smear over white. They are tuned separately.
 */

export interface ZonePaint {
  fill: string;
  stroke: string;
  label: string;
}

/** The speak point is a marker, not a wash — every part of it is stated. */
export interface SpeakPointPaint {
  fill: string;
  ring: string;
  innerRing: string;
  icon: string;
  labelFill: string;
  labelText: string;
}

export interface FloorPalette {
  /** The slab the map is drawn on, so the floor reads as an object. */
  surface: string;
  /** The rule between tiles — present, never a texture. */
  grid: string;
  zone: Record<string, ZonePaint>;
  deskAssigned: string;
  deskFree: string;
  circle: SpeakPointPaint;
  /** How the same marker looks while somebody is standing on it. */
  circleActive: SpeakPointPaint;
  /** Name plates: a chip behind the name so it survives any zone under it. */
  plateFill: string;
  plateStroke: string;
  plateText: string;
  status: Record<OfficeStatus, string>;
}

const LIGHT: FloorPalette = {
  // Off-white, not the page's own white: the floor has to read as a slab the
  // office sits on, and a border alone does not carry that.
  surface: "#f7f7f6",
  grid: "rgba(100,116,139,0.16)",
  zone: {
    open: { fill: "rgba(148,163,184,0.12)", stroke: "rgba(100,116,139,0.45)", label: "#475569" },
    focus: { fill: "rgba(139,92,246,0.12)", stroke: "rgba(139,92,246,0.45)", label: "#6d28d9" },
    meeting: { fill: "rgba(245,158,11,0.12)", stroke: "rgba(217,119,6,0.45)", label: "#92400e" },
    break: { fill: "rgba(14,165,233,0.12)", stroke: "rgba(2,132,199,0.45)", label: "#0369a1" },
  },
  deskAssigned: "rgba(71,85,105,0.38)",
  deskFree: "rgba(100,116,139,0.20)",
  circle: {
    fill: "rgba(217,119,6,0.10)",
    ring: "rgba(180,83,9,0.55)",
    innerRing: "rgba(180,83,9,0.22)",
    icon: "rgba(146,64,14,0.85)",
    labelFill: "rgba(255,251,235,0.95)",
    labelText: "#92400e",
  },
  circleActive: {
    fill: "rgba(217,119,6,0.22)",
    ring: "#b45309",
    innerRing: "rgba(180,83,9,0.40)",
    icon: "#92400e",
    labelFill: "#b45309",
    labelText: "#fffbeb",
  },
  plateFill: "rgba(15,23,42,0.80)",
  plateStroke: "rgba(15,23,42,0.80)",
  plateText: "#f8fafc",
  status: STATUS_HEX,
};

const DARK: FloorPalette = {
  // A step above the page's near-black, for the same reason.
  surface: "#151515",
  grid: "rgba(226,232,240,0.09)",
  zone: {
    open: { fill: "rgba(148,163,184,0.10)", stroke: "rgba(148,163,184,0.40)", label: "#cbd5e1" },
    focus: { fill: "rgba(139,92,246,0.16)", stroke: "rgba(167,139,250,0.55)", label: "#c4b5fd" },
    meeting: { fill: "rgba(245,158,11,0.16)", stroke: "rgba(251,191,36,0.55)", label: "#fcd34d" },
    break: { fill: "rgba(14,165,233,0.16)", stroke: "rgba(56,189,248,0.55)", label: "#7dd3fc" },
  },
  deskAssigned: "rgba(226,232,240,0.34)",
  deskFree: "rgba(148,163,184,0.20)",
  circle: {
    fill: "rgba(251,191,36,0.10)",
    ring: "rgba(251,191,36,0.55)",
    innerRing: "rgba(251,191,36,0.22)",
    icon: "rgba(253,224,71,0.90)",
    labelFill: "rgba(9,9,11,0.88)",
    labelText: "#fcd34d",
  },
  circleActive: {
    fill: "rgba(251,191,36,0.24)",
    ring: "#fcd34d",
    innerRing: "rgba(251,191,36,0.45)",
    icon: "#fde68a",
    labelFill: "#b45309",
    labelText: "#fffbeb",
  },
  // A plate has to separate from whatever is under it. On a dark floor a dark
  // chip alone does not, so it carries a hairline.
  plateFill: "rgba(9,9,11,0.88)",
  plateStroke: "rgba(255,255,255,0.22)",
  plateText: "#f5f5f5",
  status: STATUS_HEX_DARK,
};

export const FLOOR_PALETTE: Record<ThemeMode, FloorPalette> = { light: LIGHT, dark: DARK };

export function useFloorPalette(): FloorPalette {
  return FLOOR_PALETTE[useThemeMode()];
}

// ===========================================================================
// The 2D floor map (FloorCanvas) — the design's look.
//
// The palette above still drives the 3D floor, the building and the plan
// editor; the 2D map was restyled to the design (weco2/homework-office.jsx
// `HW_ZONE_STYLE`, desk cards, the Announcement Circle, name pills) and has its
// own palette so that restyle cannot repaint the 3D views by accident.
//
// The design is light only, so every value it states has a dark counterpart
// chosen separately. Brand-coloured parts are written against `--accent-rgb` /
// `--text-link`, never a literal hue, so tenant branding repaints them;
// `DEFAULT_BRAND` is what those resolve to unbranded, which is what
// `__tests__/theme.test.ts` checks contrast against.
// ===========================================================================

/** The three looks the design gives a zone. */
export type ZoneLook = "open" | "break" | "room";

/**
 * Our four zone types onto the design's three looks.
 *
 * Meeting and focus rooms both take the design's "room" look: both are a closed
 * room inside the floor with a door you knock on (focus) or a call you join
 * (meeting), which is exactly what the design's orange rooms are.
 */
export function zoneLook(type: ZoneType): ZoneLook {
  if (type === "break") return "break";
  if (type === "meeting" || type === "focus") return "room";
  return "open";
}

export interface MapZonePaint {
  fill: string;
  /** The 2px border. */
  stroke: string;
  label: string;
}

export interface DeskPaint {
  fill: string;
  border: string;
  shadow: string;
  label: string;
}

/** The Announcement Circle. CSS strings; brand parts go through tokens. */
export interface CirclePaint {
  /** Inner stop of the radial wash — what the label actually sits on. */
  fillInner: string;
  /** Outer stop, at 70%. */
  fillOuter: string;
  border: string;
  /** `dashed` idle, `solid` while somebody is standing on it. */
  borderStyle: "dashed" | "solid";
  ink: string;
  glow: string;
}

export interface PlatePaint {
  fill: string;
  text: string;
  border: string;
}

export interface MapPalette {
  /**
   * Roughly what the map card shows through — the glass over the page. Not
   * painted; it is what the translucent colours below are judged against.
   */
  backdrop: string;
  /** The floor itself, under the grid. */
  surface: string;
  /** The rule between tiles. */
  grid: string;
  zone: Record<ZoneLook, MapZonePaint>;
  desk: DeskPaint;
  circle: CirclePaint;
  /** Somebody is standing on it — the room is theirs. */
  circleActive: CirclePaint;
  /** Everybody else's name pill. */
  plate: PlatePaint;
  /** Yours, which reads "You". */
  plateMe: PlatePaint;
  plateShadow: string;
  /** Minimap: the frame showing what the viewport currently covers. */
  minimapViewport: string;
  /** Minimap: the halo around your own dot. */
  minimapMeRing: string;
  status: Record<OfficeStatus, string>;
}

/** What `--accent-rgb` and `--text-link` resolve to with no tenant branding. */
export const DEFAULT_BRAND: Record<ThemeMode, { accentRgb: string; link: string }> = {
  light: { accentRgb: "51,102,255", link: "#1F47F5" },
  dark: { accentRgb: "89,142,255", link: "#8EB6FF" },
};

const MAP_LIGHT: MapPalette = {
  backdrop: "#fbfcff",
  surface: "rgba(255,255,255,.35)",
  grid: "rgba(15,23,42,.05)",
  zone: {
    open: { fill: "rgba(255,255,255,.4)", stroke: "rgba(100,116,139,.35)", label: "#334155" },
    break: { fill: "rgba(34,211,238,.14)", stroke: "rgba(8,145,178,.35)", label: "#155e75" },
    room: { fill: "rgba(251,146,60,.13)", stroke: "rgba(234,88,12,.35)", label: "#9a3412" },
  },
  desk: {
    fill: "#ffffff",
    border: "rgba(15,23,42,.1)",
    shadow: "0 2px 4px -2px rgba(30,41,99,.15)",
    label: "#64748B",
  },
  circle: {
    fillInner: "rgba(var(--accent-rgb),.22)",
    fillOuter: "rgba(139,92,246,.10)",
    border: "rgba(var(--accent-rgb),.55)",
    borderStyle: "dashed",
    ink: "var(--text-link)",
    glow: "none",
  },
  circleActive: {
    fillInner: "rgba(var(--accent-rgb),.24)",
    fillOuter: "rgba(139,92,246,.18)",
    border: "rgba(var(--accent-rgb),.9)",
    borderStyle: "solid",
    ink: "var(--text-link)",
    glow: "0 0 0 6px rgba(var(--accent-rgb),.14)",
  },
  plate: { fill: "rgba(255,255,255,.92)", text: "#0F172A", border: "transparent" },
  plateMe: { fill: "#0F172A", text: "#ffffff", border: "transparent" },
  plateShadow: "0 2px 6px -2px rgba(30,41,99,.25)",
  minimapViewport: "#0F172A",
  minimapMeRing: "#ffffff",
  status: STATUS_HEX,
};

const MAP_DARK: MapPalette = {
  backdrop: "#191e2d",
  surface: "rgba(255,255,255,.03)",
  grid: "rgba(226,232,240,.06)",
  zone: {
    open: { fill: "rgba(148,163,184,.08)", stroke: "rgba(148,163,184,.38)", label: "#cbd5e1" },
    break: { fill: "rgba(34,211,238,.10)", stroke: "rgba(34,211,238,.42)", label: "#67e8f9" },
    room: { fill: "rgba(251,146,60,.10)", stroke: "rgba(251,146,60,.42)", label: "#fdba74" },
  },
  desk: {
    fill: "#262b3b",
    border: "rgba(255,255,255,.12)",
    shadow: "0 2px 4px -2px rgba(0,0,0,.55)",
    label: "#b4bccb",
  },
  circle: {
    fillInner: "rgba(var(--accent-rgb),.20)",
    fillOuter: "rgba(167,139,250,.08)",
    border: "rgba(var(--accent-rgb),.6)",
    borderStyle: "dashed",
    ink: "var(--text-link)",
    glow: "none",
  },
  circleActive: {
    fillInner: "rgba(var(--accent-rgb),.24)",
    fillOuter: "rgba(167,139,250,.16)",
    border: "rgba(var(--accent-rgb),.95)",
    borderStyle: "solid",
    ink: "var(--text-link)",
    glow: "0 0 0 6px rgba(var(--accent-rgb),.18)",
  },
  // A light pill on a dark floor would be the loudest thing on the map, so
  // everyone else gets a dark one with a hairline — and yours inverts.
  plate: { fill: "rgba(24,24,27,.92)", text: "#f4f4f5", border: "rgba(255,255,255,.14)" },
  plateMe: { fill: "#f8fafc", text: "#0F172A", border: "transparent" },
  plateShadow: "0 2px 6px -2px rgba(0,0,0,.6)",
  minimapViewport: "#f8fafc",
  minimapMeRing: "#0B1020",
  status: STATUS_HEX_DARK,
};

export const MAP_PALETTE: Record<ThemeMode, MapPalette> = { light: MAP_LIGHT, dark: MAP_DARK };

export function useMapPalette(): MapPalette {
  return MAP_PALETTE[useThemeMode()];
}

/**
 * The design's pastel avatar backgrounds (`HW_AV_BG`). Initials sit on them in
 * one dark ink in both themes — the disc carries its own background, so it is
 * the disc, not the page, that the ink has to clear.
 */
export const AVATAR_PASTELS = ["#dbe4ff", "#e9ddff", "#d5f5e8", "#ffe1ec", "#ffe8d1", "#d6f1f7"] as const;
export const AVATAR_PASTEL_INK = "#1E293B";

/** Resolve the brand tokens a palette value uses to their unbranded default. */
export function resolveDefaultBrand(css: string, mode: ThemeMode): string {
  const brand = DEFAULT_BRAND[mode];
  return css.replace(/var\(--accent-rgb\)/g, brand.accentRgb).replace(/var\(--text-link\)/g, brand.link);
}
